import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import {
  useAcceptOrderMutation,
  useGetUserOrderQuery,
} from "../../api/bookings/bookingsApiSlice";
import { RootState } from "../../redux/store";
import { DEFAULT_PAYMENT_TERM_DAYS, resolveLegalText } from "./postpayLegalText";
import { acceptanceScreenState } from "./acceptanceState";
import { serviceLabelPath } from "./serviceLabel";
import { useDataProtectionPath } from "../bookLocation/common/hooks/useDataProtectionPath";
import { useInPlaceTerms } from "../bookLocation/common/hooks/useTenantTermsPage";
import HintsDialog from "../hints/components/HintsDialog";
import { buildAppUrl } from "../../util";
import PageShell from "../layout/components/PageShell";
import KolulaStorageManager from "../../storage/KolulaStorageManager";
import useServiceStorage from "../../hooks/useServiceStorage";
import { ServiceTypeSelectorOption } from "../../types/order";
import routes from "../../routes/routes";
import { useBookingTimeZone } from "../../util/locationTimeZone";

type AcceptanceOutcome =
  | { kind: "idle" }
  | { kind: "concluded"; alreadyConcluded: boolean }
  | { kind: "rejected"; reason: string };

const isKnownService = (
  service: string | undefined | null
): service is ServiceTypeSelectorOption =>
  Object.values(ServiceTypeSelectorOption).includes(
    service as ServiceTypeSelectorOption
  );

/**
 * Postpay acceptance — the customer's "Kostenpflichtig buchen" click (#1702,
 * spec §5.3). Reached only from the offer email's deep link:
 *
 *   {domain}/orders/accept?order_id={uuid}&t={token}
 *
 * UNAUTHENTICATED by design. Possession of the order id plus a single-purpose
 * token is the bearer model, the same one the reservation deep link uses (D7);
 * the token is what makes this stronger than merely VIEWING a booking, which
 * the id alone already permits.
 *
 * The click itself is the acceptance act — there are no per-document
 * checkboxes (decision A9). What the screen owes the customer before that click
 * is the reservation they are agreeing to, the contract terms, and a button
 * whose label says it costs money (§ 312j BGB).
 */
const AcceptOrderPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("order_id") ?? "";
  const token = searchParams.get("t") ?? "";

  const config = useSelector((state: RootState) => state.tenantConfig.config);
  // The terms are shown the way the customer-data step shows them: the
  // tenant's own terms page, IN PLACE, in a sheet held open by a search param
  // (#2555). Navigating away would take the acceptance token out of the
  // address bar, and a customer who came back to a dead link could no longer
  // conclude the contract — the sheet keeps the route, and with it `order_id`
  // and `t`, mounted.
  //
  // The link used to be a raw `<a href="/hints...">`. A raw anchor bypasses
  // React Router's basename, so on the shared deployment (`/{slug}/...`) it
  // dropped the tenant, the first path segment `hints` was read as the slug,
  // and every postpay customer landed on "This booking page doesn't exist".
  const { TermsPage, termsOpen, openTerms, closeTerms } = useInPlaceTerms();
  // Tenants with no component-based terms page (legacy tenants, whose hint
  // pages come from the backend list) keep a link — through `buildAppUrl`, so
  // it carries the basename, and in a new tab, so the token stays here.
  const termsPath = useDataProtectionPath();
  const [acceptOrder, { isLoading: isAccepting }] = useAcceptOrderMutation();
  const [outcome, setOutcome] = useState<AcceptanceOutcome>({ kind: "idle" });
  // The Buchungen tab this device is on. Writing it here is how a concluded
  // booking lands on ITS use case's tab instead of whichever one the selector
  // would otherwise restore (#2352) — the same session key the tab chips write
  // when the customer picks a tab themselves.
  const { updateService } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.RENTAL
  );

  // Restore the reservation from the URL, not from whatever the app happened to
  // have selected: this page is normally opened cold, from an email, on a
  // device that has never seen the booking (deep-link rule).
  const {
    data: orders,
    isFetching,
    isError,
  } = useGetUserOrderQuery({ orderIds: orderId }, { skip: !orderId });

  const order = useMemo(
    () => (Array.isArray(orders) ? orders[0] : undefined),
    [orders]
  );
  // The order's own location clock (#3219), from its payload — this page is
  // opened cold, so it must not depend on anything the app selected.
  const timeZone = useBookingTimeZone(order);

  // #1702/#2318: decide what this link can still do BEFORE offering a button.
  // Until now the screen rendered the reservation and the CTA for any hydrated
  // order and only learned it was refused after the click — so a guest whose
  // booking had been cancelled, or whose offer lapsed days ago, was shown a
  // live-looking offer and found out by pressing a button that says it costs
  // money. The backend still re-checks on submit; this only stops the screen
  // inviting a click it can already tell will fail.
  const screenState = order
    ? acceptanceScreenState(order, config?.offerValidityDays ?? null, Date.now())
    : "acceptable";

  useEffect(() => {
    if (screenState === "concluded" && outcome.kind === "idle") {
      setOutcome({ kind: "concluded", alreadyConcluded: true });
    }
  }, [screenState, outcome.kind]);

  const lang = i18n.language?.startsWith("de") ? "de" : "en";

  /**
   * The tenant's own wording where it saved one, else kolula's default from
   * i18n.
   *
   * MIRROR: the defaults must match `backend/src/core/lib/email/postpayBlocks.ts`,
   * which renders the same sentences into the acceptance email. Screen and mail
   * stating different contract terms is the one outcome this must not produce —
   * so the override is read from the same stored value the mail reads, and so is
   * the payment target below: `paymentTermDays` comes from the tenant config the
   * mail composes with, resolved server-side. It used to be baked into the i18n
   * sentence as a literal "14", which told a 30-day tenant's customer something
   * their own offer mail contradicted (#2352).
   */
  const legalText = (
    field: "paymentTerms" | "noWithdrawal" | "agbNote",
    fallbackKey: string,
    options?: Record<string, unknown>
  ): string =>
    resolveLegalText(
      config?.legalTexts,
      i18n.language,
      field,
      t(fallbackKey, options ?? {})
    );

  const paymentTermDays = config?.paymentTermDays ?? DEFAULT_PAYMENT_TERM_DAYS;

  const submit = async () => {
    if (isAccepting) return;
    try {
      const result: { alreadyConcluded?: boolean } = await acceptOrder({
        orderId,
        token,
      }).unwrap();
      setOutcome({
        kind: "concluded",
        alreadyConcluded: result?.alreadyConcluded === true,
      });
    } catch (error) {
      // The backend names WHY in `msg` — slot started, canceled, archived, bad
      // token. Each needs a different sentence: "this link is no longer valid"
      // would be wrong for a booking the customer cancelled themselves.
      //
      // `details.msg`, NOT `msg`: `processError` serialises an AppError as
      // `{ message, details }` and the reason rides in `details` (the error's
      // 4th constructor argument). Reading it one level too high silently
      // yielded `undefined`, so every rejection fell through to the generic
      // sentence and none of the specific ones could ever appear.
      const reason =
        (error as { data?: { details?: { msg?: string } } })?.data?.details
          ?.msg ?? "unknown";
      setOutcome({ kind: "rejected", reason });
    }
  };

  const concluded = outcome.kind === "concluded";
  const orderService = order?.service;
  const persistedRef = useRef(false);

  /**
   * A concluded booking has to exist on this device (#2352). `neuland` and
   * every other `authentication: "none"` tenant list Buchungen from the order
   * ids in IndexedDB, written only on the `/orders?order_id=…` path — and the
   * acceptance link is a different route, so the customer concluded a contract
   * and then found an empty Buchungen screen.
   *
   * Runs for BOTH ways of ending up concluded: the click, and re-opening a link
   * whose offer was already accepted. The write is the same idempotent upsert
   * OrdersPage does, and it fails open — a device that refuses IndexedDB still
   * gets the ticket through the CTA below, which re-enters that path.
   */
  useEffect(() => {
    if (!concluded || !orderId || persistedRef.current) return;
    persistedRef.current = true;
    if (isKnownService(orderService)) updateService(orderService);
    void (async () => {
      try {
        await new KolulaStorageManager().insertInternalMultipleOrdersIds([
          orderId,
        ]);
      } catch (error) {
        // A device that refuses IndexedDB (private mode) must still see its
        // confirmation — the CTA re-enters the path that stores the id.
        console.error("Failed to store the concluded booking on device:", error);
      }
    })();
  }, [concluded, orderId, orderService, updateService]);

  // The deep-link form, so OrdersPage hydrates and focuses THIS booking rather
  // than whatever the list happens to hold.
  const goToBookings = useCallback(() => {
    navigate(`${routes.orders}?order_id=${encodeURIComponent(orderId)}`);
  }, [navigate, orderId]);

  const missingLink = !orderId || !token;

  if (missingLink) {
    return (
      <Message
        title={t("postpay_acceptance.invalid_link_title")}
        body={t("postpay_acceptance.invalid_link_body")}
      />
    );
  }

  if (isFetching) {
    // A static line reads as a hung screen — in testing this state stood for
    // ~40 seconds. The spinner is the app's standard one.
    return (
      <PageShell title={t("postpay_acceptance.title")}>
        <div className="flex flex-col items-center gap-4 py-12">
          <Spinner size="lg" ariaLabel={t("common.loading")} />
          <p className="text-sm text-foreground/60">
            {t("postpay_acceptance.loading")}
          </p>
        </div>
      </PageShell>
    );
  }

  // Entity-gated page: never paint a partial screen. Without the order there is
  // nothing to agree to.
  if (isError || !order) {
    return (
      <Message
        title={t("postpay_acceptance.not_found_title")}
        body={t("postpay_acceptance.not_found_body")}
      />
    );
  }

  if (outcome.kind === "concluded") {
    return (
      <Message
        title={t("postpay_acceptance.confirmed_title")}
        body={
          outcome.alreadyConcluded
            ? t("postpay_acceptance.already_confirmed_body")
            : t("postpay_acceptance.confirmed_body")
        }
        action={
          <Button
            size="lg"
            variant="primary"
            className="w-full"
            onClick={goToBookings}
          >
            {t("postpay_acceptance.go_to_bookings")}
          </Button>
        }
      />
    );
  }

  if (outcome.kind === "rejected") {
    return (
      <Message
        title={t("postpay_acceptance.rejected_title")}
        body={t(`postpay_acceptance.rejected_${outcome.reason}`, {
          defaultValue: t("postpay_acceptance.rejected_unknown"),
        })}
      />
    );
  }

  // Refused on open — each state gets its own sentence, because they are not
  // interchangeable. Cancellation additionally points at the mail we sent
  // (#2347): the screen only makes sense as the second piece of evidence.
  if (screenState !== "acceptable" && screenState !== "concluded") {
    return (
      <Message
        title={t(`postpay_acceptance.state_${screenState}_title`)}
        body={t(`postpay_acceptance.state_${screenState}_body`)}
      />
    );
  }

  const item = order.items?.[0];

  return (
    <PageShell title={t("postpay_acceptance.title")}>
      <div className="rounded-xl border border-foreground/10 bg-background px-4 py-2 shadow-sm">
        <Row
          label={t("postpay_acceptance.booking_number")}
          value={order.bookingNumber}
        />
        <Row
          label={t("postpay_acceptance.service")}
          // The raw identifier ("venue") used to reach the customer here.
          value={order.service ? t(serviceLabelPath(order.service)) : undefined}
        />
        <Row
          label={t("postpay_acceptance.from")}
          value={formatDate(item?.fromDate, lang, timeZone)}
        />
        <Row
          label={t("postpay_acceptance.to")}
          value={formatDate(item?.toDate, lang, timeZone)}
        />
      </div>

      <div className="flex flex-col gap-3 text-sm text-foreground/70">
        <p>
          {legalText("paymentTerms", "postpay_acceptance.default_payment_terms", {
            days: paymentTermDays,
          })}
        </p>
        <p>
          {legalText("noWithdrawal", "postpay_acceptance.default_no_withdrawal")}
        </p>
        <p>
          {legalText("agbNote", "postpay_acceptance.default_agb_note")}{" "}
          {TermsPage ? (
            <button
              type="button"
              onClick={openTerms}
              className="font-semibold text-primary underline"
            >
              {t("postpay_acceptance.read_terms")}
            </button>
          ) : (
            <a
              href={buildAppUrl(termsPath)}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-primary underline"
            >
              {t("postpay_acceptance.read_terms")}
            </a>
          )}
        </p>
      </div>

      {TermsPage && (
        <HintsDialog open={termsOpen} onClose={closeTerms} showFaqsBack={false}>
          <TermsPage lang={i18n.language} />
        </HintsDialog>
      )}

      <Button
        size="lg"
        variant="primary"
        className="w-full"
        onClick={submit}
        disabled={isAccepting}
      >
        {t("postpay_acceptance.cta")}
      </Button>
    </PageShell>
  );
};

const Row = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex justify-between gap-4 border-b border-foreground/5 py-3 text-sm last:border-b-0">
    <span className="text-foreground/60">{label}</span>
    <span className="text-right font-semibold text-foreground/90">
      {value || "-"}
    </span>
  </div>
);

const Message = ({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) => (
  <PageShell title={title}>
    {body && <p className="text-sm text-foreground/70">{body}</p>}
    {action}
  </PageShell>
);

// On the station's clock (ADR-0019 rule 2) — the order's location zone
// (#3219) — not the phone's: the customer accepts the booking window the
// station will honour.
const formatDate = (value: string | undefined, lang: string, timeZone: string): string =>
  value
    ? new Date(value).toLocaleString(lang === "de" ? "de-DE" : "en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone,
      })
    : "";

export default AcceptOrderPage;
